import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Navbar } from './Navbar';
import { RequestsSidebar } from './RequestsSidebar';
import { RequestDetail } from './RequestDetail';
import { PatientDetail } from './PatientDetail';
import { SettingsPage } from './SettingsPage';
import { MicConfigurationPage } from './MicConfigurationPage';
import { SupportModal } from './SupportModal';
import { UserRequest, Account } from '../types';
import { getBrandName } from '../utils/brandUtils';
import { api } from '../services/api';

interface MainLayoutProps {
  children: React.ReactNode;
  newRequest?: UserRequest | null;
  onNewRequest?: (request: UserRequest | null) => void;
  account: Account;
  onAccountUpdate: (account: Account) => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ 
  children, 
  newRequest, 
  onNewRequest,
  account,
  onAccountUpdate
}) => {
  const auth = usePlatformAuth();
  const navigate = useNavigate();
  const [selectedRequest, setSelectedRequest] = useState<UserRequest | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [detailView, setDetailView] = useState<'request' | 'patient' | null>(null);
  const [previousDetailView, setPreviousDetailView] = useState<'request' | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'requests'>('upload');
  const [isMobile, setIsMobile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMicConfiguration, setShowMicConfiguration] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [updatedRequest, setUpdatedRequest] = useState<UserRequest | null>(null);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for mic configuration events
  useEffect(() => {
    const handleOpenMicConfiguration = () => {
      setShowMicConfiguration(true);
    };

    window.addEventListener('openMicConfiguration' as any, handleOpenMicConfiguration);
    
    return () => {
      window.removeEventListener('openMicConfiguration' as any, handleOpenMicConfiguration);
    };
  }, []);

  // Listen for settings events
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
    };

    window.addEventListener('openSettings' as any, handleOpenSettings);
    
    return () => {
      window.removeEventListener('openSettings' as any, handleOpenSettings);
    };
  }, []);

  // Auto-switch to requests tab when new request is created on mobile
  useEffect(() => {
    if (newRequest && isMobile) {
      setActiveTab('requests');
    }
  }, [newRequest, isMobile]);

  // Clear updatedRequest after it's been processed by sidebar
  useEffect(() => {
    if (updatedRequest) {
      const timer = setTimeout(() => {
        setUpdatedRequest(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [updatedRequest]);

  const handleRequestSelect = (request: UserRequest) => {
    setSelectedRequest(request);
    setSelectedPatientId(null);
    setPreviousDetailView(null);
    setDetailView('request');
    setActiveTab('requests');
  };

  const handleRequestUpdate = (updatedRequest: UserRequest) => {
    // Update the selected request if it's the same one
    if (selectedRequest?.id === updatedRequest.id) {
      setSelectedRequest(updatedRequest);
    }
    // Notify sidebar to update its list by setting a new object reference
    setUpdatedRequest({ ...updatedRequest });
  };

  const handleBackToRequests = () => {
    setSelectedRequest(null);
    setSelectedPatientId(null);
    setDetailView(null);
    setPreviousDetailView(null);
  };

  const handlePatientSelect = (patientId: string, _request: UserRequest) => {
    if (!patientId) return;
    setPreviousDetailView(detailView === 'request' ? 'request' : null);
    setSelectedPatientId(patientId);
    setDetailView('patient');
    setActiveTab('requests');
  };

  const handleBackFromPatientDetail = () => {
    if (previousDetailView === 'request' && selectedRequest) {
      setDetailView('request');
    } else {
      setDetailView(null);
    }
    setSelectedPatientId(null);
    setPreviousDetailView(null);
  };

  const handleRequestSelectById = async (requestId: string) => {
    try {
      const request = await api.getUserRequest(requestId);
      handleRequestSelect(request);
    } catch (error) {
      console.error('Error fetching request:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Navigate anyway to clear state
      navigate('/');
    }
  };

  const handleProfile = () => {
    // TODO: Implement profile functionality
    console.log('Profile clicked');
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleTabChange = (tab: 'upload' | 'requests') => {
    setActiveTab(tab);
    if (tab === 'upload') {
      setSelectedRequest(null);
      setSelectedPatientId(null);
      setDetailView(null);
      setPreviousDetailView(null);
    }
  };

  const handleBackFromSettings = () => {
    setShowSettings(false);
  };

  const handleBackFromMicConfiguration = () => {
    setShowMicConfiguration(false);
  };

  const handleSupportSubmit = async (message: string) => {
    try {
      await api.createSupportRequest(account.id, message);
      console.log('Support request submitted successfully');
      // Could add a success toast notification here
    } catch (error) {
      console.error('Failed to submit support request:', error);
      throw error;
    }
  };

  return (
    <div className="app app-shell min-h-screen flex flex-col">
      <Navbar 
        onLogout={handleLogout}
        onProfile={handleProfile}
        onUpgrade={handleUpgrade}
        onSettings={() => setShowSettings(true)}
        onMicConfiguration={() => setShowMicConfiguration(true)}
        account={account}
      />
      
      {isMobile ? (
        // Mobile Layout with Tabs
        <div className="flex-1 flex flex-col bg-base-100">
          {/* Mobile Tab Navigation */}
          <div className="flex bg-base-100 border-b border-base-300 sticky top-0 z-50">
            <button
              className={`
                flex-1 flex flex-col items-center gap-1 py-3 px-2 text-sm font-medium transition-all duration-200
                ${activeTab === 'upload' 
                  ? 'text-primary border-b-2 border-primary bg-primary/5' 
                  : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
                }
              `}
              onClick={() => handleTabChange('upload')}
            >
              <svg className="w-5 h-5 stroke-current stroke-2 fill-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              New Visit
            </button>
            <button
              className={`
                flex-1 flex flex-col items-center gap-1 py-3 px-2 text-sm font-medium transition-all duration-200
                ${activeTab === 'requests' 
                  ? 'text-primary border-b-2 border-primary bg-primary/5' 
                  : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
                }
              `}
              onClick={() => handleTabChange('requests')}
            >
              <svg className="w-5 h-5 stroke-current stroke-2 fill-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Visits
            </button>
          </div>

          {/* Mobile Tab Content */}
          <div className="flex-1 overflow-y-auto bg-base-100 mx-2 md:mx-0">
            {showSettings ? (
              <SettingsPage 
                onBack={handleBackFromSettings}
                account={account}
                onAccountUpdate={onAccountUpdate}
                onUpgrade={handleUpgrade}
                onOpenSupport={() => setShowSupportModal(true)}
              />
            ) : activeTab === 'upload' ? (
              <div className="h-full flex flex-col">
                {children}
              </div>
            ) : activeTab === 'requests' ? (
              <div className="h-full flex flex-col">
                {detailView === 'patient' && selectedPatientId ? (
                  <PatientDetail
                    patientId={selectedPatientId}
                    onBack={handleBackFromPatientDetail}
                    onRequestSelect={handleRequestSelectById}
                  />
                ) : selectedRequest && detailView !== 'patient' ? (
                  <RequestDetail 
                    key={selectedRequest.id}
                    request={selectedRequest}
                    onBack={handleBackToRequests}
                    onPatientClick={handlePatientSelect}
                    onRequestUpdate={handleRequestUpdate}
                  />
                ) : (
                  <div className="h-full overflow-y-auto">
                    <RequestsSidebar 
                      selectedRequestId={undefined}
                      onRequestSelect={handleRequestSelect}
                      newRequest={newRequest || undefined}
                      onNewRequestAdded={() => onNewRequest?.(null)}
                      onPatientSelect={handlePatientSelect}
                      updatedRequest={updatedRequest || undefined}
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        // Desktop Layout with Sidebar
        <div className="flex-1 flex h-[calc(100vh-64px)]">
          <div className="w-[350px] border-r border-base-300 bg-base-100 overflow-y-auto">
            <RequestsSidebar 
              selectedRequestId={selectedRequest?.id}
              onRequestSelect={handleRequestSelect}
              newRequest={newRequest || undefined}
              onNewRequestAdded={() => onNewRequest?.(null)}
              onPatientSelect={handlePatientSelect}
              updatedRequest={updatedRequest || undefined}
            />
          </div>
          
          <div className="flex-1 overflow-y-auto bg-base-100 mx-2 md:mx-0">
            {showSettings ? (
              <SettingsPage 
                onBack={handleBackFromSettings}
                account={account}
                onAccountUpdate={onAccountUpdate}
                onUpgrade={handleUpgrade}
                onOpenSupport={() => setShowSupportModal(true)}
              />
            ) : detailView === 'patient' && selectedPatientId ? (
              <PatientDetail
                patientId={selectedPatientId}
                onBack={handleBackFromPatientDetail}
                onRequestSelect={handleRequestSelectById}
              />
            ) : selectedRequest && detailView !== 'patient' ? (
              <RequestDetail 
                key={selectedRequest.id}
                request={selectedRequest}
                onBack={handleBackToRequests}
                onPatientClick={handlePatientSelect}
                onRequestUpdate={handleRequestUpdate}
              />
            ) : (
              children
            )}
          </div>
        </div>
      )}

      {/* Mic Configuration Modal Overlay */}
      {showMicConfiguration && (
        <MicConfigurationPage 
          onBack={handleBackFromMicConfiguration}
        />
      )}
      
      {/* Footer */}
      <footer className="mt-auto py-4 text-center text-sm text-base-content/60 border-t border-base-300 bg-base-100">
        © 2025 {getBrandName()} · <a href="/privacy" className="text-base-content/60 hover:text-base-content hover:underline">Privacy Policy</a> · <a href="/terms" className="text-base-content/60 hover:text-base-content hover:underline">Terms</a>
      </footer>

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        onSubmit={handleSupportSubmit}
      />

      {/* Floating Help Button */}
      <button
        onClick={() => setShowSupportModal(true)}
        className="btn btn-circle btn-primary fixed bottom-6 right-6 shadow-lg z-40 hover:scale-110 transition-transform"
        aria-label="Get Help"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
    </div>
  );
}; 